/* eslint-disable react-refresh/only-export-components, max-lines -- A design-system showcase route
   colocates its demo components with the route export by design: each demo exists to be read
   alongside the prose describing it, and splitting them into sibling files would put the example
   and its explanation in different places. Length is the point here, not bloat. */
import {
  DimeEditableCell,
  type DimeEditableCellProps,
  DimeSeparator,
  DimeTable,
  DimeTableBody,
  DimeTableCell,
  DimeTableHead,
  DimeTableHeader,
  DimeTableRow,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/editable-cell')({
  component: EditableCellDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function formatDollars(value: number | null) {
  return value == null ? '' : `$${value.toFixed(2)}`;
}

function parseDollars(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed.replace(/[^0-9.-]/g, ''));
  return Number.isNaN(parsed) ? null : parsed;
}

// A small, self-contained price sheet: three category rows, two term columns, one row inherits
// from its parent, one carries a locked override. Mirrors the real shape from NUDOOL-27's price
// sheet (§4A) closely enough to be a meaningful demo without pulling in that feature's own API.
interface DemoRow {
  id: string;
  category: string;
  cod: { value: number | null; state: 'set' | 'inherited' | 'empty'; locked?: boolean };
  net: { value: number | null; state: 'set' | 'inherited' | 'empty'; locked?: boolean };
}

const INITIAL_ROWS: DemoRow[] = [
  {
    id: 'flower',
    category: 'Flower',
    cod: { value: 15, state: 'set' },
    net: { value: 18, state: 'set' },
  },
  {
    id: 'pre-roll',
    category: 'Pre-Roll',
    cod: { value: 15, state: 'inherited' },
    net: { value: 18, state: 'inherited' },
  },
  {
    id: 'demo-001',
    category: 'DEMO-001 Widget',
    cod: { value: 50, state: 'set', locked: true },
    net: { value: 50, state: 'set', locked: true },
  },
  {
    id: 'concentrates',
    category: 'Concentrates',
    cod: { value: null, state: 'empty' },
    net: { value: null, state: 'empty' },
  },
];

type CellKey = 'cod' | 'net';

function BasicDemo() {
  const [rows, setRows] = React.useState(INITIAL_ROWS);

  function commit(rowId: string, column: CellKey, next: number | null) {
    setRows((current) =>
      current.map((row) =>
        row.id === rowId
          ? { ...row, [column]: { ...row[column], value: next, state: 'set' } }
          : row,
      ),
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-border">
      <DimeTable>
        <DimeTableHeader>
          <DimeTableRow>
            <DimeTableHead>Category</DimeTableHead>
            <DimeTableHead>COD</DimeTableHead>
            <DimeTableHead>NET</DimeTableHead>
          </DimeTableRow>
        </DimeTableHeader>
        <DimeTableBody>
          {rows.map((row) => (
            <DimeTableRow key={row.id}>
              <DimeTableCell>{row.category}</DimeTableCell>
              {(['cod', 'net'] as const).map((column) => (
                <DimeTableCell key={column}>
                  <DimeEditableCell<number>
                    value={row[column].value}
                    state={row[column].state}
                    locked={row[column].locked}
                    inheritedFrom="from Flower · brand-wide · effective now"
                    ariaLabel={`${row.category}, ${column === 'cod' ? 'COD' : 'NET'} price`}
                    format={formatDollars}
                    parse={parseDollars}
                    onCommit={(next) => commit(row.id, column, next)}
                  />
                </DimeTableCell>
              ))}
            </DimeTableRow>
          ))}
        </DimeTableBody>
      </DimeTable>
    </div>
  );
}

function AsyncCommitDemo() {
  const [value, setValue] = React.useState<number | null>(18);
  const [isSaving, setIsSaving] = React.useState(false);

  async function commit(next: number | null) {
    setIsSaving(true);
    // Simulates a round trip -- the cell stays open and disabled for the whole window rather
    // than collapsing early and showing the old value until this resolves.
    await new Promise((resolve) => setTimeout(resolve, 600));
    setValue(next);
    setIsSaving(false);
  }

  return (
    <div className="w-48 overflow-hidden rounded-md border border-border">
      <DimeEditableCell<number>
        value={value}
        state="set"
        ariaLabel="Flower, Net 30 price"
        format={formatDollars}
        parse={parseDollars}
        onCommit={commit}
        isSaving={isSaving}
      />
    </div>
  );
}

function ErrorDemo() {
  return (
    <div className="w-48 overflow-hidden rounded-md border border-border">
      <DimeEditableCell<number>
        value={20}
        state="set"
        ariaLabel="Flower, Net 30 price"
        format={formatDollars}
        parse={parseDollars}
        onCommit={() => {}}
        error="Another price for this selection already covers this date range."
      />
    </div>
  );
}

function DisabledDemo() {
  return (
    <div className="w-48 overflow-hidden rounded-md border border-border">
      <DimeEditableCell<number>
        value={20}
        state="set"
        ariaLabel="Flower, Net 30 price"
        format={formatDollars}
        parse={parseDollars}
        onCommit={() => {}}
        disabled
        disabledTooltip="Reactivate this category's price rules before editing."
      />
    </div>
  );
}

// Source of truth is DimeEditableCellProps. The assertion after this array fails to compile if a
// prop is added there without a row here, so the docs table cannot silently fall behind.
const PROP_ROWS = [
  { prop: 'value', type: 'TValue | null', desc: "Required. The cell's current value." },
  {
    prop: 'onCommit',
    type: '(next: TValue | null) => void | Promise<void>',
    desc: 'Required. Fired on commit with the parsed value, or null when cleared.',
  },
  {
    prop: 'format',
    type: '(value: TValue | null) => string',
    desc: 'Required. Renders value as display text; also seeds the editing draft.',
  },
  {
    prop: 'parse',
    type: '(raw: string) => TValue | null',
    desc: 'Required. Parses the edit-mode text back into TValue.',
  },
  {
    prop: 'state',
    type: "'set' | 'inherited' | 'empty'",
    desc: 'Required. Drives the display grammar — see "Rendering by state" below.',
  },
  {
    prop: 'locked',
    type: 'boolean',
    desc: 'Prefixes a lock glyph and folds "(locked)" into the accessible name.',
  },
  { prop: 'disabled', type: 'boolean', desc: 'Prevents entering edit mode.' },
  {
    prop: 'disabledTooltip',
    type: 'string',
    desc: 'Shown in a tooltip when disabled — e.g. a missing permission.',
  },
  {
    prop: 'ariaLabel',
    type: 'string',
    desc: 'Required. A bare "Price" is useless across dozens of cells — be specific.',
  },
  {
    prop: 'isSaving',
    type: 'boolean',
    desc: 'True while a commit is in flight. Keeps the cell in edit mode, disabled, instead of collapsing early.',
  },
  { prop: 'error', type: 'string', desc: 'A field-level error, rendered inline via aria-invalid.' },
  { prop: 'required', type: 'boolean', desc: 'Sets aria-required on the edit input.' },
  {
    prop: 'inheritedFrom',
    type: 'string',
    desc: 'Tooltip content naming the source of an inherited value. Ignored unless state is inherited.',
  },
  {
    prop: 'emptyText',
    type: 'string',
    desc: 'Display text for an empty cell. Defaults to an em dash.',
  },
  {
    prop: 'sanitizeInput',
    type: '(text: string) => string',
    desc: 'Filters each edit-mode change before it becomes the draft, e.g. to drop letters from a numeric cell.',
  },
  {
    prop: 'onDraftChange',
    type: '(text: string) => void',
    desc: 'Fires on every edit-mode change with the sanitized text, before any commit.',
  },
  {
    prop: 'onCancel',
    type: '() => void',
    desc: 'Fires when Escape discards the draft, so a caller that staged it can drop it.',
  },
  {
    prop: 'commitUnchanged',
    type: 'boolean',
    desc: 'Commits text the user retyped even when it equals the current value.',
  },
  { prop: 'className', type: 'string', desc: 'Additional classes on the rendered button/input.' },
] as const;

type DocumentedProp = (typeof PROP_ROWS)[number]['prop'];
type UndocumentedProp = Exclude<keyof DimeEditableCellProps<number>, DocumentedProp>;
// [T] extends [never] rather than T extends never: a naked type parameter distributes, so a union
// would resolve to never and accept true, making this assertion decorative.
const _allPropsDocumented: [UndocumentedProp] extends [never] ? true : UndocumentedProp = true;
void _allPropsDocumented;

function EditableCellDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Editable Cell</h1>
      <p className="mb-8 text-muted-foreground">
        Click-to-edit cell for <code className="font-mono">DimeTreeTable</code>/
        <code className="font-mono">DimeDataTable</code> column defs — a button in display mode, an
        input in edit mode. Built for the price sheet (NUDOOL-27 §9/R5), but currency-agnostic:
        <code className="font-mono">format</code>/<code className="font-mono">parse</code> decide
        what the value means.
      </p>

      <DocsSection title="Rendering by state">
        <BasicDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          Flower is <strong>set</strong> here. Pre-Roll is <strong>inherited</strong> — muted and
          parenthesized, with the source in a tooltip on hover. DEMO-001 Widget is locked (note the
          glyph, and try tabbing to it — its accessible name includes &ldquo;(locked)&rdquo;).
          Concentrates is <strong>empty</strong> — an em dash. Click any cell to edit it, or focus
          it and press Enter; commit with Enter or by clicking away, or press Escape to discard. Tab
          commits and moves to the next cell in the row.
        </p>
      </DocsSection>

      <DocsSection title="isSaving: stays open, doesn't collapse">
        <AsyncCommitDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          Edit the value and commit — the input stays open and disabled for about a second
          (simulating a mutation round trip) instead of collapsing back into the button and showing
          the old value until the new one arrives.
        </p>
      </DocsSection>

      <DocsSection title="Inline error">
        <ErrorDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">error</code> sets{' '}
          <code className="font-mono">aria-invalid</code> and renders the message via a
          visually-hidden, screen-reader-accessible{' '}
          <code className="font-mono">role=&quot;alert&quot;</code> element.
        </p>
      </DocsSection>

      <DocsSection title="Disabled">
        <DisabledDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          Hover the cell to see <code className="font-mono">disabledTooltip</code> — the same
          pattern <code className="font-mono">DimeButton</code> uses.
        </p>
      </DocsSection>

      <DocsSection title="Props">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Prop</th>
              <th className="py-2 pr-4 font-medium">Type</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {PROP_ROWS.map((row) => (
              <tr key={row.prop} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{row.prop}</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">{row.type}</td>
                <td className="py-2 text-muted-foreground">{row.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>
    </div>
  );
}
